import React, { Fragment } from 'react';
import { TextField } from '@cimpress/react-components';

import { generatePreview } from '../../services/renderingClient';
import { getBasicTransientUdsDocument } from '../../services/udsClient';
import CimdocModal from './cimdocModal';
import ImageLoader from '../ImageLoader';
import ImageSelection from '../imageSelection';
import ApiTab from '../ApiTab';
import Slider from '../Slider';

const defaultColorization = {
    hueMultiplier: 1,
    hueOffset: 0,
    saturationMultiplier: 1,
    saturationOffset: 0,
    lightnessMultiplier: 1,
    lightnessOffset: 0,
};

const extractColors = state => ({
    hueMultiplier: state.hueMultiplier,
    hueOffset: state.hueOffset,
    saturationMultiplier: state.saturationMultiplier,
    saturationOffset: state.saturationOffset,
    lightnessMultiplier: state.lightnessMultiplier,
    lightnessOffset: state.lightnessOffset,
});

export default class Recolorization extends ApiTab {
    state = {
        image: {
            url: 'https://dev.scenes.documents.cimpress.io/v2/merchants/SceneTestGroup/compositions/e22a575d-c8a5-4784-bb33-32dd4f44ea8f/assets/35411c73-b4f8-41b2-9325-f549c549518b',
            height: 236,
            width: 236,
        },
        jsonOpen: false,
        ...defaultColorization,
        url: 'https://dev.scenes.documents.cimpress.io/v2/merchants/SceneTestGroup/compositions/e22a575d-c8a5-4784-bb33-32dd4f44ea8f/assets/35411c73-b4f8-41b2-9325-f549c549518b',
    }

    setVariable(variable, value) {
        this.setState({ [variable]: value }, this.updateDoc);
    }

    updateDoc = () => {
        const { url } = this.state.image;
        let { width, height } = this.state.image;
        if (width > 500) {
            height = Math.round(height * (500 / width));
            width = 500;
        }
        const instructionsUri = getBasicTransientUdsDocument({ url, width, height, colorAdjustment: extractColors(this.state) });
        this.setState({ url: generatePreview({ instructionsUri, width, height }) });
    }

    showJson = () => {
        this.setState({ jsonOpen: true });
    }

    hideJson = () => {
        this.setState({ jsonOpen: false });
    }

    setDefault = () => {
        this.setState(Object.assign({}, defaultColorization), this.updateDoc);
    }

    setBlackAndWhite = () => {
        this.setState(Object.assign({}, defaultColorization, { saturationMultiplier: 0 }), this.updateDoc);
    }

    setHueShift = () => {
        this.setState(Object.assign({}, defaultColorization, { hueOffset: 180 }), this.updateDoc);
    }

    setSepia = () => {
        this.setState(Object.assign({}, defaultColorization, { hueOffset: 30, saturationMultiplier: 0.3, hueMultiplier: 0.05 }), this.updateDoc);
    }

    setColorPop = () => {
        this.setState(Object.assign({}, defaultColorization, { saturationMultiplier: 1.5 }), this.updateDoc);
    }

    setVintage = () => {
        this.setState(Object.assign({}, defaultColorization, { lightnessMultiplier: 0.9, lightnessOffset: 0.05, hueOffset: 10, saturationMultiplier: 0.5 }), this.updateDoc);
    }

    renderLeft = () => (
        <Fragment>
            <ImageSelection image={this.state.image.url} onSelectImage={image => this.setVariable('image', image)} getSize={true} />
            <button className='btn btn-default recolorization-button' onClick={this.setDefault}>Default</button>
            <button className='btn btn-default recolorization-button' onClick={this.setBlackAndWhite}>Black and White</button>
            <button className='btn btn-default recolorization-button' onClick={this.setHueShift}>Hue Shift</button>
            <button className='btn btn-default recolorization-button' onClick={this.setSepia}>Sepia</button>
            <button className='btn btn-default recolorization-button' onClick={this.setColorPop}>Color Pop</button>
            <button className='btn btn-default recolorization-button' onClick={this.setVintage}>Vintage</button>
            <Slider label='Hue Multiplier' max={1} {...this.valueLink('hueMultiplier', true)} increment={0.01} />
            <Slider label='Hue Offset' max={360} {...this.valueLink('hueOffset', true)} />
            <TextField label='Saturation Multiplier' value={this.state.saturationMultiplier} onChange={e => this.setVariable('saturationMultiplier', e.target.value)} type='number' />
            <Slider label='Saturation Offset' max={1} {...this.valueLink('saturationOffset', true)} increment={0.01} />
            <TextField label='Lightness Multiplier' value={this.state.lightnessMultiplier} onChange={e => this.setVariable('lightnessMultiplier', e.target.value)} type='number' />
            <Slider label='Lightness Offset' max={1} {...this.valueLink('lightnessOffset', true)} increment={0.01} />
        </Fragment>
    )

    renderTitle = () => (
        <Fragment>
            Recolorization is one of the many image manipulations allowed by the Cimdoc. For a full look at the capabilities of the Cimdoc,
            <a
                href='https://cimpress-support.atlassian.net/wiki/spaces/DDS/pages/983216/Cimpress+Document+Interchange+Format+CDIF+Parameters'
                target='_blank'
                rel='noopener noreferrer'
            > check out the docs here.</a>
        </Fragment>
    )

    renderRight = () => {
        const { hueMultiplier, hueOffset, saturationMultiplier, saturationOffset, lightnessMultiplier, lightnessOffset, jsonOpen, url } = this.state;
        return (
            <Fragment>
                <ImageLoader className='recolorization-image' url={url} alt='rendered preview' />
                <div className='rendered-fragment__label'>Recolorized Preview</div>
                <pre className='highlight-block'>
                    <code className='json hljs'>
                        <span className='hljs-attr'>colorAdjustment</span>: {'{\n    '}
                        <span className='hljs-attr'>hueMultiplier</span>: <span className='hljs-number'>{hueMultiplier}</span>{',\n    '}
                        <span className='hljs-attr'>hueOffset</span>: <span className='hljs-number'>{hueOffset}</span>{',\n    '}
                        <span className='hljs-attr'>saturationMultiplier</span>: <span className='hljs-number'>{saturationMultiplier}</span>{',\n    '}
                        <span className='hljs-attr'>saturationOffset</span>: <span className='hljs-number'>{saturationOffset}</span>{',\n    '}
                        <span className='hljs-attr'>lightnessMultiplier</span>: <span className='hljs-number'>{lightnessMultiplier}</span>{',\n    '}
                        <span className='hljs-attr'>lightnessOffset</span>: <span className='hljs-number'>{lightnessOffset}</span>{',\n'}
                        {'}'}
                    </code>
                </pre>
                <button className='btn btn-primary' onClick={this.showJson}>Show me the Cimdoc!</button>
                <CimdocModal show={jsonOpen} colorAdjustment={extractColors(this.state)} {...this.state.image} onClose={this.hideJson} />
            </Fragment>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/recolorization/index.jsx